// Raccolte Firestore condivise tra tutte le pagine della web app.
//
// Prima ogni pagina (pagamenti, ricerca, presenze, compleanni...) rileggeva
// TUTTI i documenti di una raccolta, spesso a ogni apertura o ogni pochi minuti:
// con molti allievi e piu' persone collegate si superava il limite giornaliero
// gratuito di Firestore (50.000 letture). Ora per ogni raccolta c'e' un solo
// ascolto in tempo reale, condiviso da tutte le pagine: i documenti si leggono
// una volta e poi arrivano solo quelli che cambiano.
import { collection, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';

// Quanto resta attivo l'ascolto dopo che nessuna pagina lo usa piu'
// (cosi' cambiando pagina non si rilegge tutto da capo).
const ATTESA_CHIUSURA_MS = 15 * 60 * 1000;
// Se il server non risponde (offline), dopo questo tempo si usa la copia locale.
const ATTESA_SERVER_MS = 8000;

const archivio = new Map();

function apriVoce(nome) {
  let voce = archivio.get(nome);
  if (voce) return voce;
  voce = { nome, ultimo: null, ultimoServer: null, ascoltatori: new Set(), attese: [], timerChiusura: null, annulla: null };
  archivio.set(nome, voce);
  voce.annulla = onSnapshot(collection(db, nome), snapshot => {
    voce.ultimo = snapshot;
    if (!snapshot.metadata.fromCache) voce.ultimoServer = snapshot;
    voce.ascoltatori.forEach(ascoltatore => {
      try { ascoltatore.avanti(snapshot); } catch (errore) { console.error(`Errore nella pagina che usa "${nome}":`, errore); }
    });
    if (!snapshot.metadata.fromCache) {
      const attese = voce.attese.splice(0);
      attese.forEach(attesa => attesa.risolvi(snapshot));
    }
  }, errore => {
    console.error(`Impossibile leggere la raccolta "${nome}":`, errore);
    chiudiVoce(voce);
    voce.ascoltatori.forEach(ascoltatore => { if (ascoltatore.errore) ascoltatore.errore(errore); });
    voce.attese.splice(0).forEach(attesa => attesa.rifiuta(errore));
  });
  return voce;
}

function chiudiVoce(voce) {
  if (voce.timerChiusura) window.clearTimeout(voce.timerChiusura);
  voce.timerChiusura = null;
  if (voce.annulla) voce.annulla();
  voce.annulla = null;
  if (archivio.get(voce.nome) === voce) archivio.delete(voce.nome);
}

function programmaChiusura(voce) {
  if (voce.ascoltatori.size || voce.attese.length || voce.timerChiusura) return;
  voce.timerChiusura = window.setTimeout(() => {
    voce.timerChiusura = null;
    if (!voce.ascoltatori.size && !voce.attese.length) chiudiVoce(voce);
  }, ATTESA_CHIUSURA_MS);
}

function annullaChiusura(voce) {
  if (voce.timerChiusura) window.clearTimeout(voce.timerChiusura);
  voce.timerChiusura = null;
}

// Come onSnapshot(collection(db, nome), avanti, errore), ma condiviso.
export function ascoltaRaccolta(nome, avanti, errore) {
  const voce = apriVoce(nome);
  annullaChiusura(voce);
  const ascoltatore = { avanti, errore };
  voce.ascoltatori.add(ascoltatore);
  if (voce.ultimo) {
    const snapshot = voce.ultimo;
    Promise.resolve().then(() => {
      if (voce.ascoltatori.has(ascoltatore) && voce.ultimo === snapshot) {
        try { avanti(snapshot); } catch (e) { console.error(`Errore nella pagina che usa "${nome}":`, e); }
      }
    });
  }
  return () => {
    voce.ascoltatori.delete(ascoltatore);
    programmaChiusura(voce);
  };
}

// Come getDocs(collection(db, nome)), ma usa l'ascolto condiviso:
// se e' gia' attivo restituisce subito i dati aggiornati, senza nuove letture.
export function leggiRaccolta(nome) {
  const voce = apriVoce(nome);
  annullaChiusura(voce);
  if (voce.ultimo && (voce.ultimoServer === voce.ultimo || voce.ultimoServer)) {
    const risultato = voce.ultimo;
    programmaChiusura(voce);
    return Promise.resolve(risultato);
  }
  return new Promise((risolvi, rifiuta) => {
    const attesa = {
      risolvi: snapshot => { window.clearTimeout(attesa.timer); risolvi(snapshot); programmaChiusura(voce); },
      rifiuta: errore => { window.clearTimeout(attesa.timer); rifiuta(errore); }
    };
    // Senza rete: si usa la copia salvata sul dispositivo, come faceva getDocs.
    attesa.timer = window.setTimeout(() => {
      const indice = voce.attese.indexOf(attesa);
      if (indice < 0) return;
      voce.attese.splice(indice, 1);
      if (voce.ultimo) attesa.risolvi(voce.ultimo);
      else attesa.rifiuta(new Error(`Raccolta "${nome}" non disponibile: controlla la connessione.`));
    }, ATTESA_SERVER_MS);
    voce.attese.push(attesa);
  });
}

// All'uscita (logout) si chiude tutto: i dati non restano in ascolto.
export function chiudiTutteLeRaccolte() {
  Array.from(archivio.values()).forEach(chiudiVoce);
}

onAuthStateChanged(auth, utente => { if (!utente) chiudiTutteLeRaccolte(); });
